import {
  createFileRoute,
  Link,
  Outlet,
  useMatchRoute,
} from "@tanstack/react-router";
import { useClosedDays, type ClosedDay } from "@/lib/pos-store";
import { fmt } from "@/components/AppShell";
import {
  Calendar,
  ChevronRight,
  Receipt,
  Banknote,
  ArrowDownUp,
} from "lucide-react";

export const Route = createFileRoute("/reports/closed")({
  head: () => ({
    meta: [
      { title: "Closed Days" },
      {
        name: "description",
        content: "List of all previously closed day reports.",
      },
    ],
  }),
  component: ClosedDaysList,
});

function ClosedDaysList() {
  const closedDays = useClosedDays();
  const matchRoute = useMatchRoute();
  const isExact = matchRoute({ to: "/reports/closed", fuzzy: false });

  // Only show the list when on exact /reports/closed (not child routes like /reports/closed/2026-05-28)
  if (!isExact) return <Outlet />;

  return (
    <div className="px-4 pt-4">
      {closedDays.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Receipt className="mb-3 h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm font-medium text-muted-foreground">
            No closed days yet
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            Close a day from the Cashbook to see it here.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {closedDays.map((d, i) => (
            <Link
              key={d.date + "-" + i}
              to="/reports/closed/$date"
              params={{ date: d.date }}
              className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 active:scale-[0.98] transition-transform"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">{formatDate(d.date)}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Closed {timeAgo(d.closedAt)}
                </p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Banknote className="h-3 w-3" />
                    Sales: {fmt(d.salesTotal)}
                  </span>
                  <span className="flex items-center gap-1">
                    <ArrowDownUp className="h-3 w-3" />
                    BCF: {fmt(d.bcf)}
                  </span>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </Link>
          ))}
        </div>
      )}
      <Outlet />
    </div>
  );
}

function formatDate(iso: string) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-KE", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function timeAgo(ts: number) {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
